import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { authApi } from '../api/endpoints';
import { restoreSession, setAccessToken, setUnauthorizedHandler } from '../api/client';
import { can, canAny } from '../utils/permissions';
import { clearClientStorage } from '../utils/clientStorage';

// Exported so a test can supply a stub value directly; application code should
// use the useAuth hook below rather than consuming the context itself.
export const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  // `loading` covers the initial silent refresh, so the router does not bounce
  // an already-signed-in user to /login while that request is in flight.
  const [loading, setLoading] = useState(true);

  const signOutLocal = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    clearClientStorage();
  }, []);

  // A 401 that survives a refresh attempt means the session is over.
  useEffect(() => {
    setUnauthorizedHandler(signOutLocal);
  }, [signOutLocal]);

  /**
   * On boot, try to exchange the refresh cookie for a session. Failure is the
   * normal path for a signed-out visitor, so it is not surfaced as an error.
   *
   * Goes through `restoreSession` rather than calling the endpoint directly, because the
   * API rotates the refresh token and destroys the old one as it is exchanged. React
   * StrictMode double-invokes this effect in development, so a direct call fires two
   * refreshes: the first rotates the token and the second presents one that no longer
   * exists, gets a 401, and signs the user straight back out. `restoreSession` shares a
   * single in-flight request, so both invocations resolve from one exchange.
   *
   * The `cancelled` flag below guards the state updates but NOT the request — which is
   * why de-duplicating at the request layer is the actual fix.
   */
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const payload = await restoreSession();
        if (cancelled) return;
        setAccessToken(payload.accessToken);
        setUser(payload.user);
      } catch {
        if (!cancelled) signOutLocal();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [signOutLocal]);

  const login = useCallback(async (credentials) => {
    const { data } = await authApi.login(credentials);
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Even if the call fails, the local session must end.
    }
    signOutLocal();
  }, [signOutLocal]);

  const acceptInvite = useCallback(async (payload) => {
    const { data } = await authApi.acceptInvite(payload);
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  /** Re-reads the profile after a role or profile change. */
  const refreshUser = useCallback(async () => {
    try {
      const { data } = await authApi.me();
      setUser(data);
      return data;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo(() => {
    const permissions = user?.role?.permissions || [];
    return {
      user,
      loading,
      isAuthenticated: Boolean(user),
      permissions,
      can: (permission) => can(permissions, permission),
      canAny: (list) => canAny(permissions, list),
      login,
      logout,
      acceptInvite,
      refreshUser,
    };
  }, [user, loading, login, logout, acceptInvite, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside an AuthProvider');
  return ctx;
}
